import React, { ChangeEvent } from 'react';
import {
  Connector,
  CredentialFormFields,
  IConnectorHostURLProps,
  IConnectorCredentialsFormProps,
  IConnectorActionsGroupProps,
} from '../connectors-types';
import { Stack } from '@mui/material';
import CredentialsTab from '../components/CredentialsTab';
import { CredentialsType } from '../connector-configure-types';
import { openRepoSelectionModal } from '../actions/connector-configure-actions';
import { useSnapshot } from 'valtio';
import ScanStore from '../../new-scan/store/scan-store';
import { useIsUserReadOnlyRole } from '../../members/api/user-roles-api';

const ConnectorCredentialsFormContainer: React.FC<
  React.PropsWithChildren<ConnectorCredentialsFormProps>
> = props => {
  const {
    values,
    connector,
    handleOnChange,
    onVerifyCredentials,
    onRemoveCredentials,
    isConnectorConfigured,
    connectorHostURLProps,
    credentialsExpirationMsg,
    areCredentialsExpired,
    validateConnector,
    connectorValidationInProgress,
    showSingleConnectorValidationMsg,
    isScanning,
  } = props;

  const { isDemoEnabled } = useSnapshot(ScanStore);
  const isReadOnlyRole = useIsUserReadOnlyRole();

  const connectorCredentialsFormProps: IConnectorCredentialsFormProps = {
    handleOnChange,
    values,
    isConnectorConfigured,
    credentialsExpirationMsg,
    areCredentialsExpired,
    errorMessage: connector.errorMessage,
    connector,
    tokenLabelField:
      connector.credentialsType === CredentialsType.ApiKey ? 'ApiKey' : 'Token',
    tenantFieldRequired:
      connector.credentialsType === CredentialsType.UserPasswordAndTenant,
    onpremAWSCredentialsRequired:
      connector.credentialsType === CredentialsType.AWSAssumeRoleOnprem ||
      connector.credentialsType ===
        CredentialsType.AWSAssumeRoleCloudFormationOnprem ||
      connector.credentialsType ===
        CredentialsType.AWSOrganizationConnectionOnprem,
    isReadOnlyRole: isDemoEnabled || isReadOnlyRole,
  };

  const connectorActionsGroupProps: IConnectorActionsGroupProps = {
    onRemoveCredentials,
    onVerifyAndAdd: onVerifyCredentials,
    validateConnector,
    connectorValidationInProgress,
    openConnectorSettings: openRepoSelectionModal,
  };

  return (
    <Stack
      direction='column'
      justifyContent='flex-end'
      sx={{ width: '100%', typography: 'body1' }}>
      {connector.credentialsType !== CredentialsType.None && (
        <CredentialsTab
          connector={connector}
          connectorHostURLProps={connectorHostURLProps}
          connectorCredentialsFormProps={connectorCredentialsFormProps}
          connectorActionsGroupProps={connectorActionsGroupProps}
          isConnectorConfigured={isConnectorConfigured}
          showSingleValidationMsg={showSingleConnectorValidationMsg.shouldShow}
          connectorIsValid={showSingleConnectorValidationMsg.isValid}
          isScanning={isScanning}
        />
      )}
    </Stack>
  );
};

interface ConnectorCredentialsFormProps {
  isConnectorConfigured: boolean;
  connector: Connector;
  values: CredentialFormFields;
  handleOnChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onVerifyCredentials: () => void;
  onRemoveCredentials: () => void;
  areCredentialsExpired: boolean;
  credentialsExpirationMsg: string;
  invalidForm: boolean;
  validateConnector: () => void;
  connectorValidationInProgress: boolean;
  connectorHostURLProps?: IConnectorHostURLProps;
  showSingleConnectorValidationMsg: {
    shouldShow: boolean;
    isValid: boolean;
  };
  isScanning: boolean;
}

export default ConnectorCredentialsFormContainer;
